/* ============================================================================
   <mini-cart> — desktop hover preview for the header cart pill.

   Open / close is driven entirely by CSS (.tk-minicart:hover / :focus-within in
   assets/mini-cart.css), so the popover appears even if this script is slow or
   fails. The panel markup (snippets/mini-cart.liquid) is rendered INLINE in the
   header, so it is instant on hover and always reflects the true cart (every
   add-to-cart in this theme full-navigates to /cart).

   This element only handles the interactive bits:
     • in-popup removal via the Cart Change API, with a loading overlay and a
       Section-Rendering-API re-render of the panel (?section_id=mini-cart)
     • keeping the header count + panel in sync with cart updates fired elsewhere
       (e.g. removing a line on the /cart page) via the cartUpdate pub/sub event
     • reflecting open state on the pill's aria-expanded

   Globals (loaded site-wide before this defer script): routes, fetchConfig
   (global.js), subscribe / publish + PUB_SUB_EVENTS (pubsub.js / constants.js).
   Each is used defensively so a missing one never breaks the popover.
   ========================================================================== */
if (!customElements.get('mini-cart')) {
  customElements.define(
    'mini-cart',
    class MiniCart extends HTMLElement {
      connectedCallback() {
        this.pill = this.querySelector('.tk-cart-pill');
        this.panel = this.querySelector('[data-mini-cart-panel]');
        this.busy = false;

        this.addEventListener('click', this.onClick.bind(this));

        // Reflect open state for assistive tech (non-blocking — CSS does the work).
        const expand = (val) => this.pill && this.pill.setAttribute('aria-expanded', val);
        this.addEventListener('mouseenter', () => expand('true'));
        this.addEventListener('mouseleave', () => !this.busy && expand('false'));
        this.addEventListener('focusin', () => expand('true'));
        this.addEventListener('focusout', (e) => !this.contains(e.relatedTarget) && expand('false'));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      /* ---------- removal ---------- */

      onClick(event) {
        const removeBtn = event.target.closest('[data-mini-cart-remove]');
        if (!removeBtn || !this.contains(removeBtn)) return;
        event.preventDefault();
        this.removeLine(removeBtn.dataset.line);
      }

      removeLine(line) {
        if (this.busy || !line || typeof routes === 'undefined') return;
        this.busy = true;
        this.classList.add('is-loading'); // CSS keeps the panel open + shows the spinner

        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const body = JSON.stringify({
          line: Number(line),
          quantity: 0,
          sections: 'mini-cart',
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...config, body })
          .then((response) => response.json())
          .then((state) => {
            if (state.sections && state.sections['mini-cart']) {
              this.renderPanel(state.sections['mini-cart']);
            }
            this.updateCount(state.item_count);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart', cartData: state });
            }
          })
          .catch((error) => console.error('mini-cart remove failed', error))
          .finally(() => {
            this.busy = false;
            this.classList.remove('is-loading');
          });
      }

      /* ---------- external cart updates (e.g. the /cart page) ---------- */

      onCartUpdate(event) {
        if (!event || event.source === 'mini-cart') return; // ignore our own
        if (event.cartData && typeof event.cartData.item_count === 'number') {
          this.updateCount(event.cartData.item_count);
        }
        if (typeof routes === 'undefined') return;
        fetch(`${routes.cart_url}?section_id=mini-cart`)
          .then((response) => response.text())
          .then((html) => this.renderPanel(html))
          .catch((error) => console.error('mini-cart refresh failed', error));
      }

      /* ---------- helpers ---------- */

      // Swap fresh panel innerHTML from a rendered section, keeping the live
      // <mini-cart> element (and therefore its hover/open state).
      renderPanel(html) {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mini-cart-panel]');
        if (fresh && this.panel) this.panel.innerHTML = fresh.innerHTML;
      }

      updateCount(count) {
        if (typeof count !== 'number') return;
        this.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = count;
          // Drives the mobile count badge: hidden at zero (see alcell-header).
          el.classList.toggle('is-empty', count === 0);
        });
      }
    }
  );
}
